import { TFunction } from 'i18next';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { InsightFilter, InsightOperator } from '@/shared/types/analytics';
import { Insight } from '@/shared/types/insights';

import { DEFAULT_OPERATORS, OPERATOR_LABELS } from './constants';

const getPlaceholder = (insightType: string, t: TFunction) => {
  switch (insightType) {
    case 'email':
      return t('filters.placeholders.email');
    case 'phone_number':
      return t('filters.placeholders.phone_number');
    default:
      return t('filters.placeholders.default');
  }
};

interface StringInsightFilterProps {
  filterIndex: number;
  insight: Insight;
}

export const StringInsightFilter = ({ filterIndex, insight }: StringInsightFilterProps) => {
  const { getValues, control } = useFormContext();
  const { t } = useTranslation();
  const { update } = useFieldArray({ control, name: 'filters' });

  // Get the insightFilters array
  const insightFilters = useWatch({ control, name: `filters.${filterIndex}.insightFilters` }) || [];

  // Check if this insight already exists in the filters
  const existingFilterIndex = insightFilters.findIndex(
    (filter: InsightFilter) => filter.id === insight.name
  );

  const currentFilter =
    existingFilterIndex !== -1
      ? insightFilters[existingFilterIndex]
      : {
          id: insight.name,
          category: insight.type,
          operator: DEFAULT_OPERATORS[insight.type] || 'eq',
          values: [],
        };

  const updateFilter = (newInsightFilter: InsightFilter) => {
    const updatedFilters = [...insightFilters];

    if (existingFilterIndex !== -1) {
      // Update existing filter
      updatedFilters[existingFilterIndex] = newInsightFilter;
    } else {
      // Add new filter
      updatedFilters.push(newInsightFilter);
    }

    const currentFormFilter = getValues(`filters.${filterIndex}`);
    update(filterIndex, { ...currentFormFilter, insightFilters: updatedFilters });
  };

  const handleOperatorChange = (newOperator: InsightOperator) => {
    updateFilter({ ...currentFilter, operator: newOperator });
  };

  const handleValueChange = (newValue: string) => {
    const trimmedValue = newValue.trim();

    if (trimmedValue.length > 0) {
      // Update with meaningful value
      updateFilter({ ...currentFilter, values: [newValue] });
    } else if (existingFilterIndex !== -1) {
      // Remove the filter if value becomes empty and filter exists
      const updatedFilters = [...insightFilters];
      updatedFilters.splice(existingFilterIndex, 1);

      const currentFormFilter = getValues(`filters.${filterIndex}`);
      update(filterIndex, { ...currentFormFilter, insightFilters: updatedFilters });
    }
  };

  const operatorOptions = OPERATOR_LABELS[insight.type] || {};
  const isExistsOperator = currentFilter.operator === 'exists';

  return (
    <div className="space-y-3">
      <div role="presentation" onMouseDown={(e) => e.stopPropagation()}>
        <Select value={currentFilter.operator} onValueChange={handleOperatorChange}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(operatorOptions).map(([op, label]) => (
              <SelectItem key={op} value={op}>
                {t(label as string)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!isExistsOperator ? (
        <Input
          type="text"
          value={currentFilter.values[0] || ''}
          onChange={(e) => handleValueChange(e.target.value)}
          placeholder={getPlaceholder(insight.type, t)}
          className="w-full"
        />
      ) : (
        <div className="rounded-md border border-border bg-muted p-2 text-sm text-foreground-muted">
          {t('filters.exists_description', 'Any non-null value')}
        </div>
      )}
    </div>
  );
};
